/**
 * Зеркало backend `SeoConfigDto`. Бэкенд отдаёт это в `ApiResponseDetails<SeoConfig>` по
 * `GET /api/v1/seo/config` (под anon-token, как `/catalog/tree`), т.е. Node берёт `.data`.
 *
 * НЕ менять в одностороннем порядке — синхронизировать с Java `SeoConfigDto`. Поля строго
 * camelCase, совпадают по именам с бэком.
 *
 * Type-only модуль — безопасно импортировать и из Node SSR (`SeoConfigCache`), и из
 * Angular через DI-токен `SEO_CONFIG` (APP_INITIALIZER + TransferState).
 */

/** Коды редиректа, дефолт 301. */
export type RedirectType = 301 | 302 | 307 | 308;

/** Organization JSON-LD. `null`, если не сконфигурирован — схему не инжектим. */
export interface SeoOrganization {
  name: string | null;
  logoUrl: string | null;
  /** Ссылки на соцсети (schema.org sameAs). */
  sameAs: string[];
  /** Телефон → ContactPoint. Опционально — бэк может не отдавать (старый конфиг). */
  telephone?: string | null;
  /** Email → ContactPoint.email. */
  email?: string | null;
  /** Адрес одной строкой → PostalAddress.streetAddress. */
  address?: string | null;
}

/** Управляемые мета-дефолты, применяются `SeoMetaService` при SSR. */
export interface SeoMetaDefaults {
  /** Пусто → фронт берёт fallback из `/init` (`StoreService.getSiteName()`). */
  siteName: string | null;
  /** Шаблон `<title>`, напр. `{pageTitle} — {siteName}`. */
  titleTemplate: string | null;
  description: string | null;
  /** Абсолютный URL дефолтного og:image. */
  ogImage: string | null;
  /** Напр. `ru_RU`. */
  ogLocale: string | null;
  /** Дефолтный `og:type` для страниц без своего (admin-форма пишет `defaultOgType`). */
  ogType: 'website' | 'article' | 'product' | null;
  /** Глобальный дефолт robots-meta (напр. `index,follow`); per-page override — через `SeoData.robots`. */
  defaultRobotsMeta: string | null;
  /** Verification-теги вебмастеров; пусто → тег не ставим. */
  yandexVerification: string | null;
  googleVerification: string | null;
  org: SeoOrganization | null;
  /** Флаги расширений — генерация JSON-LD из данных страницы. */
  breadcrumbJsonLd: boolean;
  productJsonLd: boolean;
  /** Allowlist значимых query-ключей для canonical; seed `['id','uid','page']`. */
  canonicalKeepQueryKeys: string[];
}

/** Активное правило редиректа. Только `is_active` и не истёкшие приходят в конфиг. */
export interface SeoRedirect {
  /** Нормализованный path без query. */
  fromUrl: string;
  toUrl: string;
  type: RedirectType;
  /** Wildcard-правило (приоритет: exact > wildcard). */
  isWildcard: boolean;
  /** Тай-брейк среди wildcard-правил. */
  sortOrder: number;
  /** ISO-дата авто-отключения; `null` — бессрочно. */
  expiresAt: string | null;
}

/** Частота изменения для sitemap (sitemaps.org changefreq). */
export type SitemapChangefreq =
  | 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';

/** Параметры одного типа карты. */
export interface SitemapTypeConfig {
  enabled: boolean;
  priority: number; // 0.0–1.0
  changefreq: SitemapChangefreq;
}

/** Управление sitemap (правится админ-вкладкой, едет в /seo/config). */
export interface SeoSitemapConfig {
  enabled: boolean;             // глобальный on/off
  pageSize: number;             // товаров на файл (дефолт 250, кламп 50–2000)
  products: SitemapTypeConfig;
  categories: SitemapTypeConfig;
  pages: SitemapTypeConfig;     // меню + новости + акции + ручные + статика
  lists: SitemapTypeConfig;     // SEO-подборки (docs/seo-tz-list-pages.md)
  excludeOutOfStock: boolean;   // → /product/filter?hasStock=true
  excludeUrlPatterns: string[]; // wildcard-исключения итоговых path'ов
  manualUrls: string[];         // принудительно добавляемые path'ы в pages
}

/**
 * Аналитика и счётчики — инжектятся в `<head>` витрины в браузере (ТЗ-2 §2.4).
 * Пустые значения = выключено. Правится админ-формой «Общие настройки», едет в /seo/config.
 */
export interface SeoAnalytics {
  /** Google Analytics 4 Measurement ID, напр. `G-XXXXXXXXXX`. Пусто → не подключаем. */
  ga4Id: string | null;
  /** Google Tag Manager container ID, напр. `GTM-XXXXXXX`. */
  gtmId: string | null;
  /** Номера счётчиков Яндекс.Метрики (поддержка нескольких). */
  yandexMetrika: number[];
  /** Включить e-commerce dataLayer в инициализации Метрики. */
  yandexMetrikaEcommerce: boolean;
}

/**
 * Типы страниц, у которых есть управляемые мета-дефолты (SEO-11).
 *
 * Ключи совпадают с `page_type` в БД и с ключами карты в конфиге — регистр значим.
 * Список закрыт кодом витрины: новый тип появляется вместе с компонентом, который
 * передаёт его в `SeoMetaService.apply()`, а не заведением строки в справочнике.
 */
export type SeoPageType =
  | 'home' | 'category' | 'product'
  | 'news' | 'newsList' | 'promo' | 'promoList'
  | 'text' | 'listPage' | 'listIndex';

/**
 * Мета-дефолты типа страниц: уровень между полем записи и глобальными дефолтами.
 * Пустое поле означает «работает уровень выше», а не «пустое значение».
 */
export interface SeoPageTypeMeta {
  /** Шаблон `<title>` с `%s`; у `home` и страниц-списков — готовая строка. */
  title: string | null;
  /** Шаблон `description` с `%s`; у `home` и страниц-списков — готовая строка. */
  description: string | null;
  /** Шаблон видимого `<h1>`; на карточках подставляется название сущности. */
  h1: string | null;
}

/** Мета главной страницы (ТЗ-2 §2.2). Главная = `/` (SEO-8: кабинет переехал в корень). */
export interface SeoHomePage {
  title: string | null;
  description: string | null;
  /** Текст видимого `<h1>` на главной; пусто → H1 не рендерим. */
  h1: string | null;
}

/** Настройки пагинации (ТЗ-2 §2.8). */
export interface SeoPaginationConfig {
  /** Эмитить `<link rel="next/prev">` на страницах листинга. */
  relNextPrev: boolean;
}

/** Корневой объект конфига (payload `ApiResponseDetails.data`). */
export interface SeoConfig {
  /** Серийник конфига — растёт при любой правке; Node-кэш использует для cache-bust. */
  version: number;
  /** Полный origin `https://host`; `null` = канон-редирект выключен. */
  canonicalHost: string | null;
  /** `false` (staging) → robots `Disallow: /` + meta `noindex,nofollow` форсятся. */
  searchIndexable: boolean;
  /** Граница пагинации для canonical; `null` = без лимита. */
  maxIndexablePage: number | null;
  /** Готовое тело robots.txt (активная версия). */
  robots: string;
  metaDefaults: SeoMetaDefaults;
  redirects: SeoRedirect[];
  /** Управление sitemap; `undefined` у старого бэка → Node берёт дефолты. */
  sitemap?: SeoSitemapConfig;
  /** Аналитика/счётчики; `undefined` у старого бэка → ничего не инжектим. */
  analytics?: SeoAnalytics;
  /**
   * Мета-дефолты по типам страниц (SEO-11). `undefined` у старого бэка → работают только
   * глобальные дефолты, то есть поведение до фичи.
   */
  pageTypeMeta?: Partial<Record<SeoPageType, SeoPageTypeMeta>>;
  /**
   * Мета главной (dashboard); `undefined` → берутся глобальные дефолты.
   *
   * @deprecated Заменён `pageTypeMeta.home`. Читается как fallback, пока обе стороны не
   * раскатаны: Node-кэш держит конфиг до минуты, и в момент деплоя приходит ответ старого
   * бэка. Удаляется вместе с колонками `seo_settings.home_page_*`.
   */
  homePage?: SeoHomePage;
  /** Пагинация; `undefined` → rel next/prev выключены. */
  pagination?: SeoPaginationConfig;
}

/**
 * Безопасный дефолт (single source): сайт индексируется, канон-редирект выключен,
 * правил нет, seed canonicalKeepQueryKeys. Используется Node-кэшем (`SeoConfigCache`)
 * при недоступном бэке и Angular-фабрикой `seoConfigFactory` при пустом TransferState.
 * Это единственное значение в файле — остальное type-only.
 */
export const SEO_CONFIG_FALLBACK: SeoConfig = {
  version: 0,
  canonicalHost: null,
  searchIndexable: true,
  maxIndexablePage: null,
  robots: '',
  metaDefaults: {
    siteName: null,
    titleTemplate: null,
    description: null,
    ogImage: null,
    ogLocale: null,
    ogType: null,
    defaultRobotsMeta: null,
    yandexVerification: null,
    googleVerification: null,
    org: null,
    breadcrumbJsonLd: false,
    productJsonLd: false,
    // `catalog_id` убран (SEO-8, ТЗ §7): идентификатор категории уехал из query в
    // path, поэтому в canonical его сохранять больше нечего — а оставленный ключ
    // сделал бы канонической вариативную форму `/catalog/12?catalog_id=12`.
    canonicalKeepQueryKeys: ['id', 'uid', 'page'],
  },
  redirects: [],
  sitemap: {
    enabled: true,
    pageSize: 250,
    products: { enabled: true, priority: 0.8, changefreq: 'daily' },
    categories: { enabled: true, priority: 0.7, changefreq: 'weekly' },
    pages: { enabled: true, priority: 0.6, changefreq: 'weekly' },
    lists: { enabled: true, priority: 0.7, changefreq: 'weekly' },
    excludeOutOfStock: false,
    excludeUrlPatterns: [],
    manualUrls: [],
  },
  analytics: {
    ga4Id: null,
    gtmId: null,
    yandexMetrika: [],
    yandexMetrikaEcommerce: false,
  },
  // Пустая карта, а не отсутствие поля: `resolveMetaTemplates` тогда не отличает
  // «бэк не отдал» от «ничего не настроено» — поведение в обоих случаях одинаковое.
  pageTypeMeta: {},
  homePage: {
    title: null,
    description: null,
    h1: null,
  },
  pagination: {
    relNextPrev: false,
  },
};
